import { MsalProvider } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { BrowserRouter } from "react-router-dom";

import {
  getOppCookie,
  initSentry,
  loadAndInjectFonts,
  setOppCookie,
} from "@pdpp/lib-react";

import { App } from "./App";
import { BasenameContext } from "./BasenameContext";
import { Auth } from "./api/auth";
import "./index.css";
import { i18n } from "./language/i18n";
import {
  addLanguageBundlesStandalone,
  getSupportedLanguage,
} from "./language/language";
import "./lib-styles";
import { ENVIRONMENT, SENTRY_DSN, SENTRY_RELEASE } from "./utils/runtimeConfig";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Prevent refetch on window focus globally
    },
  },
});

const initializeApp = async (): Promise<void> => {
  const shouldRunSentry = SENTRY_DSN && SENTRY_RELEASE && ENVIRONMENT;
  const oppCookie = getOppCookie();
  const lang = getSupportedLanguage(oppCookie.lang);
  setOppCookie({ ...oppCookie, lang });

  if (shouldRunSentry) {
    initSentry(SENTRY_DSN, SENTRY_RELEASE, ENVIRONMENT);
  }

  await loadAndInjectFonts("en");
  await addLanguageBundlesStandalone(lang);

  const container = document.getElementById("root");

  if (container !== null) {
    createRoot(container).render(
      <StrictMode>
        <MsalProvider instance={Auth.get().msalInstance}>
          <I18nextProvider i18n={i18n}>
            <BrowserRouter>
              <QueryClientProvider client={queryClient}>
                <BasenameContext.Provider value={""}>
                  <App />
                </BasenameContext.Provider>
              </QueryClientProvider>
            </BrowserRouter>
          </I18nextProvider>
        </MsalProvider>
      </StrictMode>,
    );
  }
};

initializeApp().catch(console.error);
